/**
 * ModerationCommentContent Component
 *
 * Displays a single user comment in a styled container.
 * Typically used for user-submitted comments or notes.
 *
 * @example
 * ```tsx
 * <ModerationCommentContent comment="This is inappropriate content." />
 * ```
 */

import { Text, ListItem, Icon, IconSize, Message } from '@soteria/design-system-ve';
import type { ReactNode } from 'react';

import * as styles from './ModerationCaseCard.css';

/**
 * Props for ModerationCommentContent
 */
export interface ModerationCommentContentProps {
  /**
   * Comment text to display
   */
  comment: string;

  /**
   * Optional className for custom styling
   */
  className?: string;
}

/**
 * ModerationCommentContent Component
 *
 * Renders a user comment in an italicized styled container.
 * Supports line breaks and wraps long text appropriately.
 */
export function ModerationCommentContent({ comment, className }: ModerationCommentContentProps): ReactNode {
  if (!comment || comment.trim().length === 0) {
    return (
      <Text color="secondary" size="sm">
        No comment provided
      </Text>
    );
  }

  const combinedClassName = [styles.commentContainer, className].filter(Boolean).join(' ');

  return (
    <div className={combinedClassName}>
      <ListItem
        as="button"
        background="visible"
        label1="Comment"
        label2={comment}
        leadingIcon={<Icon icon={Message} iconSize={IconSize.Small} />}
        type="button"
      />
    </div>
  );
}

ModerationCommentContent.displayName = 'ModerationCommentContent';
